import React from 'react';
import { equals, isEmpty } from 'ramda';
import moment from 'moment';
import { Button, Modal, Image, OptionsMenu, OptionButton, OptionList, OptionItem } from '../..';
import Icon from '../../Icon/Icon';
import {
  AuthToken,
  AuthUser,
  Dependent,
  fetchDependents,
  impersonateSignIn,
  isAFamilyDependent,
  parseFamilyMember,
  showAlert,
  useAuth,
} from '../../../context/authContext';
import useFetchAsync from '../../../hooks/useFetchAsync';
import getCookie from '../../../utils/getCookie';
import getFromLocalStorage from '../../../utils/getFromLocalStorage';
import { paths } from '../../../routes';

type ModalProps = {
  user: AuthUser;
  onCloseModal: () => void;
};

function AccountSwitcherPrompt({ onCloseModal, user }: ModalProps) {
  const [, dispatch] = useAuth();
  const { data, status, error, run } = useFetchAsync();
  const [activeDependent, setActiveDependent] = React.useState<Dependent>();
  const [isImpersonating, setIsImpersonating] = React.useState(false);
  const [selectedAccount, setSelectedAccount] = React.useState<{
    authToken: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    type: 'You' | 'Dependent';
    signedAvatar?: string;
  }>();

  const manager = (isAFamilyDependent(user) ? user.familyManager : user) as AuthUser;

  /** *
   * Conditionally fetch family members if the user is on a family plan
   */
  const dependents = React.useMemo(() => {
    const tempUsrManagerAccount = JSON.parse(
      getFromLocalStorage('_river_temp_usrmanager') || '{}',
    ) as { token: string; dependents: Array<Dependent> };

    if (!data || isEmpty(tempUsrManagerAccount.dependents)) return [];

    return ((data as any)?.dependents || tempUsrManagerAccount.dependents || [])
      .map((fam: Record<string, any>) => {
        return {
          ...parseFamilyMember(fam),
          sameFamilyAddress: fam.streetAddress === user.streetAddress,
        };
      })
      .filter((dependent: Dependent) => {
        if (!dependent.patientDetails?.dateOfBirth) return false;
        const age = moment().diff(moment(dependent.patientDetails.dateOfBirth), 'years');
        return age <= 14 || !dependent.phoneNumber;
      }) as Array<Dependent>;
  }, [user.streetAddress, data]);

  React.useEffect(() => {
    const fetchingFamilyMembers = fetchDependents();
    run(fetchingFamilyMembers);
  }, [run, user]);

  React.useEffect(() => {
    if (dependents && dependents.length === 0) return;

    const findDependent =
      dependents.find(
        dep =>
          isAFamilyDependent(user) &&
          dep.email === user.email &&
          dep.phoneNumber === user.phoneNumber &&
          dep?.firstName === user.firstName,
      ) || dependents[0];

    setActiveDependent(findDependent);
  }, [dependents, user]);

  const getTempUsrManagerAccount = () => {
    let tempUsrManagerAccount = JSON.parse(
      getFromLocalStorage('_river_temp_usrmanager') || '{}',
    ) as { token: string; dependents: Array<Dependent> };

    if (!tempUsrManagerAccount?.token) {
      const authToken = JSON.parse(getCookie('_river_tokid') || '{}') as AuthToken;
      tempUsrManagerAccount = {
        token: authToken.token,
        dependents,
      };
    }

    return tempUsrManagerAccount;
  };

  const handleAccountSwitch = async () => {
    if (!selectedAccount) return;

    if (
      selectedAccount?.type === 'You' &&
      !isAFamilyDependent(user) &&
      user.firstName === selectedAccount?.firstName
    ) {
      onCloseModal();
      return;
    }

    setIsImpersonating(true);

    await impersonateSignIn(selectedAccount, getTempUsrManagerAccount(), errorMsg => {
      setIsImpersonating(false);
      onCloseModal();
      if (errorMsg) {
        showAlert(dispatch, {
          show: true,
          message: errorMsg,
          type: 'ERROR',
        });
        return;
      }

      window.location.replace(paths.HOME_URL_PATH);
    });
  };

  return (
    <Modal onClose={undefined} show onHide={onCloseModal}>
      <div className="rounded-xl relative bg-white tablet:w-[550px]">
        <div className="flex flex-col pt-10 pb-12 items-center px-6 tablet:px-12">
          <div className="text-center items-center px-2 tablet:px-0">
            <h3 className="text-river-black text-[20px] text-center font-semibold">
              Select an account to proceed
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-16 mt-10">
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={() =>
                  setSelectedAccount({
                    authToken: getTempUsrManagerAccount().token || '',
                    firstName: manager?.firstName || '',
                    lastName: manager?.lastName || '',
                    email: manager?.email || '',
                    phoneNumber: manager?.phoneNumber || '',
                    signedAvatar: manager?.signedAvatar || '',
                    type: 'You',
                  })
                }
                className={`rounded-full border-2 border-transparent ${
                  selectedAccount?.type === 'You' ? 'bg-[#72C16D] border-[#72C16D]' : ''
                } `}
              >
                <Image
                  className="w-[120px] h-[120px] tablet:h-[120px] tablet:w-[120px] rounded-full"
                  src={manager?.signedAvatar || 'image_river_avatar_placeholder.svg'}
                  alt="user avatar"
                  bgColor="#fff"
                />
              </button>
              <p
                className={`mt-4 text-[#889297] ${
                  selectedAccount?.type === 'You' ? 'text-river-black font-medium' : ''
                }`}
              >
                {`${manager?.firstName} ${manager?.lastName}`} (You)
              </p>
            </div>
            <div
              className={`flex flex-col items-center ${
                dependents.length === 0 ? 'opacity-40' : ''
              }`}
            >
              <button
                type="button"
                disabled={dependents.length === 0}
                onClick={() =>
                  setSelectedAccount({
                    authToken: activeDependent?.authToken || '',
                    firstName: activeDependent?.firstName || '',
                    lastName: activeDependent?.lastName || '',
                    email: activeDependent?.email || '',
                    phoneNumber: activeDependent?.phoneNumber || '',
                    type: 'Dependent',
                  })
                }
                className={`rounded-full border-2 border-transparent${
                  selectedAccount?.type === 'Dependent' ? 'bg-[#72C16D] border-[#72C16D]' : ''
                } `}
              >
                <Image
                  className="w-[120px] h-[120px] tablet:h-[120px] tablet:w-[120px] rounded-full"
                  src={
                    isAFamilyDependent(user) &&
                    user.signedAvatar &&
                    activeDependent?.phoneNumber === user.phoneNumber
                      ? user.signedAvatar || 'image_river_provider_avatar_placeholder.svg'
                      : 'image_river_provider_avatar_placeholder.svg'
                  }
                  alt="dependent avatar"
                  bgColor="#fff"
                />
              </button>
              <div
                className={`mt-4 text-[#889297] ${
                  selectedAccount?.type === 'Dependent' ? 'text-river-black font-medium' : ''
                }`}
              >
                <OptionsMenu>
                  <OptionButton
                    disabled={dependents.length === 0 || selectedAccount?.type !== 'Dependent'}
                    className="max-w-xs flex items-center"
                  >
                    <span className="inline-flex">
                      {activeDependent
                        ? `${activeDependent.firstName} ${activeDependent.lastName}`
                        : `Your Dependent`}
                    </span>
                    {dependents.length > 0 && (
                      <Icon id="arrowDown" className="h-5 w-5 hidden tablet:inline-flex" />
                    )}
                  </OptionButton>
                  <OptionList
                    className="z-[1000] !min-w-[200px] mt-2 right-2 rounded-xl shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)]
                      bg-white border-[0.5px] border-[#e9ecef] ring-[#e9ecef] ring-opacity-5 focus:outline-none"
                  >
                    {dependents.map((dependent, index) => (
                      <OptionItem
                        key={index}
                        className={`text-[15px] text-[#889297] font-normal ${
                          equals(activeDependent, dependent) ? 'text-river-black' : ''
                        }`}
                        onClick={() => {
                          setActiveDependent(dependent);
                          setSelectedAccount({
                            authToken: dependent.authToken || '',
                            firstName: dependent.firstName,
                            lastName: dependent.lastName,
                            email: dependent.email,
                            phoneNumber: dependent.phoneNumber,
                            type: 'Dependent',
                          });
                        }}
                      >
                        {dependent.firstName} {dependent.lastName}
                      </OptionItem>
                    ))}
                  </OptionList>
                </OptionsMenu>
              </div>
            </div>
          </div>

          <div className="mt-11 text-[#717171] text-lg tablet:text-[20px]">
            <Button
              label="Continue"
              onClick={handleAccountSwitch}
              className="disabled:!bg-[#C8CCCE]/80 disabled:!text-[#fff] 
              disabled:!opacity-100 disabled:!cursor-not-allowed"
              disabled={!selectedAccount || status === 'LOADING' || isImpersonating}
              loading={isImpersonating}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AccountSwitcherPrompt;
